import type { CrystalEffectDefinition, EmpyrealCrystalSize, EmpyrealCrystalType } from './crystals';

/**
 * The 6 essence types that mortals harvest from Crucible manifestations.
 * Each is one kind of mark that mortal life leaves on the Veil: a feeling
 * (Fury, Sorrow), a virtue (Courage), a faculty (Memory), a knowing (Truth)
 * or life itself (Resonance).
 */
export type EssenceType = 'resonance' | 'memory' | 'courage' | 'sorrow' | 'fury' | 'truth';

/**
 * An essence pool tracks the current amount of each essence type.
 * Treat as immutable — never modify a pool in place; always create a new one.
 */
export type EssencePool = Record<EssenceType, number>;

/**
 * Essence allocation represents what essences are being put into a socket for crafting.
 */
export type EssenceAllocation = EssencePool;

/**
 * A recipe definition specifies how to craft a crystal from essences.
 */
export interface RecipeDefinition {
  readonly id: string;
  readonly name: string;
  readonly size: EmpyrealCrystalSize;
  readonly crystalType: EmpyrealCrystalType;
  readonly ingredients: Partial<EssencePool>; // exact recipe
  readonly isDiscoveredByDefault: boolean; // is this a standard recipe?
  readonly description?: string;
  /** What using the crystal does. A recipe without one has no effect yet. */
  readonly effect?: CrystalEffectDefinition;
}

/**
 * One crystal being coalesced from essence. It lands in `targetSlotIndex`
 * of the carried inventory when it finishes.
 */
export interface CrystalCoalescence {
  readonly id: number;
  readonly recipe: RecipeDefinition;
  /** What it took from run essence. */
  readonly essenceAllocated: EssenceAllocation;
  /** What it took from banked essence (the player's vessel), when run essence fell short. */
  readonly bankedEssenceAllocated: EssenceAllocation;
  /** Carried inventory slot the finished crystal goes into. */
  readonly targetSlotIndex: number;
  readonly startedAtMs: number;
  readonly completesAtMs: number;
}

/**
 * Validate if a candidate is a valid EssenceType.
 */
export function isEssenceType(candidate: unknown): candidate is EssenceType {
  return (
    candidate === 'resonance' ||
    candidate === 'memory' ||
    candidate === 'courage' ||
    candidate === 'sorrow' ||
    candidate === 'fury' ||
    candidate === 'truth'
  );
}

/**
 * Get all essence types.
 */
export function getAllEssenceTypes(): readonly EssenceType[] {
  return ['resonance', 'memory', 'courage', 'sorrow', 'fury', 'truth'];
}

/** Each essence type's name as players read it. */
export const ESSENCE_TYPE_NAMES: Readonly<Record<EssenceType, string>> = {
  resonance: 'Resonance',
  memory: 'Memory',
  courage: 'Courage',
  sorrow: 'Sorrow',
  fury: 'Fury',
  truth: 'Truth',
};

/**
 * Create an empty essence pool (all zeros).
 */
export function createEmptyEssencePool(): EssencePool {
  return {
    resonance: 0,
    memory: 0,
    courage: 0,
    sorrow: 0,
    fury: 0,
    truth: 0,
  };
}

/**
 * Sum all essence in a pool.
 */
export function resolveTotalEssence(pool: Partial<EssencePool>): number {
  return (
    (pool.resonance ?? 0) +
    (pool.memory ?? 0) +
    (pool.courage ?? 0) +
    (pool.sorrow ?? 0) +
    (pool.fury ?? 0) +
    (pool.truth ?? 0)
  );
}

/**
 * Whether `pool` covers `required`: at least as much of every type it
 * asks for, a type the pool lacks counting as none (banked essence lists
 * only the types a character holds).
 */
export function hasMinimumEssence(pool: Partial<EssencePool>, required: Partial<EssencePool>): boolean {
  return getAllEssenceTypes().every((type) => (required[type] ?? 0) <= (pool[type] ?? 0));
}

/**
 * How much of each type `pool` is short of for `required` (see
 * `hasMinimumEssence`); empty when it covers it.
 */
export function resolveEssenceShortfall(pool: Partial<EssencePool>, required: Partial<EssencePool>): Partial<EssencePool> {
  const shortfall: Partial<EssencePool> = {};
  for (const type of getAllEssenceTypes()) {
    const missing = (required[type] ?? 0) - (pool[type] ?? 0);
    if (missing > 0) {
      shortfall[type] = missing;
    }
  }
  return shortfall;
}

/**
 * What a coalescence may spend: the run's essence less what is held for
 * the commission (`resolveCommissionReservedEssence`), plus the vessel's
 * banked essence. The host judges a start by it, and the run's HUD shows it.
 */
export function resolveSpendableCraftEssence(run: EssencePool, held: Partial<EssencePool>, banked: Partial<EssencePool>): EssencePool {
  return withEssenceAdded(withEssenceSubtracted(run, held), banked);
}

/**
 * Subtract essences from a pool (does not validate, assumes pool has enough).
 */
export function withEssenceSubtracted(pool: EssencePool, required: Partial<EssencePool>): EssencePool {
  return {
    resonance: pool.resonance - (required.resonance ?? 0),
    memory: pool.memory - (required.memory ?? 0),
    courage: pool.courage - (required.courage ?? 0),
    sorrow: pool.sorrow - (required.sorrow ?? 0),
    fury: pool.fury - (required.fury ?? 0),
    truth: pool.truth - (required.truth ?? 0),
  };
}

/**
 * A partial pool (an item's essence value, a cost) as a full one: each missing type is 0.
 */
export function toEssencePool(partial: Partial<EssencePool>): EssencePool {
  return withEssenceAdded(createEmptyEssencePool(), partial);
}

/**
 * Add essences to a pool.
 */
export function withEssenceAdded(pool: EssencePool, addition: Partial<EssencePool>): EssencePool {
  return {
    resonance: pool.resonance + (addition.resonance ?? 0),
    memory: pool.memory + (addition.memory ?? 0),
    courage: pool.courage + (addition.courage ?? 0),
    sorrow: pool.sorrow + (addition.sorrow ?? 0),
    fury: pool.fury + (addition.fury ?? 0),
    truth: pool.truth + (addition.truth ?? 0),
  };
}
